/**
 * Product Card Quick Add Module
 * Handles quick size selection & add-to-cart orchestration on product cards.
 */

(function () {
  'use strict';

  // =========================================================================
  // Centralized Configuration & Selectors
  // =========================================================================

  const SELECTORS = {
    card: '[data-product-card], .product-card',
    trigger: '[data-quick-add-trigger]',
    label: '[data-quick-add-label]',
    sizeButton: '[data-size-button], .product-card__size-button',
    cartDrawerItems: 'cart-items-component',
    cartBubbleTextCount: '.cart-bubble__text-count',
    cartBubbleScreenReader: '.cart-bubble .visually-hidden',
    cartBubble: '.cart-bubble',
    headerCartIcon: '.header-actions__cart-icon',
  };

  const CARD_STATES = {
    idle: 'idle',
    open: 'is-quick-add-open',
    adding: 'is-quick-add-adding',
    success: 'is-quick-add-success',
  };

  const BUTTON_STATES = {
    idle: 'idle',
    active: 'is-active',
    expanded: 'is-expanded',
    success: 'is-success',
  };

  // The theme's own cart event. cart-drawer-component listens to it to auto-open,
  // cart-items-component to refresh its contents, and cart-icon to update the bubble.
  const CART_UPDATE_EVENT = 'cart:update';
  const QUICK_ADD_SOURCE = 'product-card-quick-add';

  // =========================================================================
  // Pure Business & API Services
  // =========================================================================

  /**
   * Collects the section IDs the cart drawer needs re-rendered after a cart change.
   * Mirrors product-form.js so the theme can morph the drawer in place instead of refetching it.
   * @returns {string} Comma separated section IDs, empty when no cart drawer is present.
   */
  function getCartSectionIds() {
    const sectionIds = [];

    document.querySelectorAll(SELECTORS.cartDrawerItems).forEach((component) => {
      const sectionId = component.getAttribute('data-section-id');
      if (sectionId && !sectionIds.includes(sectionId)) {
        sectionIds.push(sectionId);
      }
    });

    return sectionIds.join(',');
  }

  /**
   * Adds a product variant to the Shopify cart.
   * @param {string|number} variantId - The variant ID to add.
   * @param {number} [quantity=1] - Quantity of variant.
   * @param {string} [sections=''] - Section ID(s) to re-render.
   * @returns {Promise<Object>} The API response payload.
   */
  async function addItemToCart(variantId, quantity = 1, sections = '') {
    if (!variantId) {
      throw new Error('Variant ID is required to add item to cart.');
    }

    const payload = {
      id: variantId,
      quantity: quantity,
    };

    if (sections) {
      payload.sections = sections;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.description || errorData.message || 'Failed to add item to cart.';
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Retrieves current cart data from Shopify /cart.js.
   * @returns {Promise<Object>} Cart data payload.
   */
  async function getCartDetails() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to retrieve cart details.');
    }
    return response.json();
  }

  // =========================================================================
  // DOM State Mutators
  // =========================================================================

  /**
   * Updates card container state classes safely.
   * @param {HTMLElement} card - The product card element.
   * @param {string} state - The target state key from CARD_STATES.
   */
  function updateCardState(card, state) {
    if (!card) return;

    card.classList.remove(CARD_STATES.open, CARD_STATES.adding, CARD_STATES.success);

    if (state === 'open') {
      card.classList.add(CARD_STATES.open);
      card.setAttribute('data-quick-add-state', 'open');
    } else if (state === 'adding') {
      card.classList.add(CARD_STATES.adding);
      card.setAttribute('data-quick-add-state', 'adding');
    } else if (state === 'success') {
      card.classList.add(CARD_STATES.success);
      card.setAttribute('data-quick-add-state', 'success');
    } else {
      card.setAttribute('data-quick-add-state', 'idle');
    }
  }

  /**
   * Updates quick add button appearance and label.
   * @param {HTMLElement} triggerBtn - The quick add button.
   * @param {string} state - The target state key.
   * @param {string} [text=''] - Label text for pill mode.
   */
  function updateTriggerButtonState(triggerBtn, state, text = '') {
    if (!triggerBtn) return;

    const labelElement = triggerBtn.querySelector(SELECTORS.label);

    triggerBtn.classList.remove(BUTTON_STATES.active, BUTTON_STATES.expanded, BUTTON_STATES.success);
    triggerBtn.setAttribute('data-state', state);

    if (state === 'open') {
      triggerBtn.classList.add(BUTTON_STATES.active);
      triggerBtn.disabled = false;
      // Reopening after an add: drop the leftover "Added to Cart" pill text.
      if (labelElement) {
        labelElement.textContent = '';
      }
    } else if (state === 'adding') {
      triggerBtn.classList.add(BUTTON_STATES.expanded);
      if (labelElement) {
        labelElement.textContent = text || 'Adding...';
      }
      triggerBtn.disabled = true;
    } else if (state === 'success') {
      triggerBtn.classList.add(BUTTON_STATES.expanded, BUTTON_STATES.success);
      if (labelElement) {
        labelElement.textContent = text || 'Added to Cart';
      }
      triggerBtn.disabled = false;
    } else {
      triggerBtn.disabled = false;
      if (labelElement) {
        labelElement.textContent = '';
      }
    }
  }

  /**
   * Announces the cart change to the rest of the theme.
   *
   * This is what makes the cart drawer refresh its contents and auto-open (when the
   * "Add to cart auto-opens drawer" setting is on), the same way the product form does.
   *
   * @param {Object} options
   * @param {Object|null} options.cart - Cart payload from /cart.js, when available.
   * @param {Object} [options.sections] - Sections HTML map returned from /cart/add.js.
   * @param {string|number} options.variantId - The variant that was added.
   * @param {number} options.itemCount - Total number of items now in the cart.
   */
  function dispatchCartUpdate({ cart, sections, variantId, itemCount }) {
    const event = new Event(CART_UPDATE_EVENT, { bubbles: true });

    event.detail = {
      resource: cart || undefined,
      sourceId: QUICK_ADD_SOURCE,
      data: {
        source: QUICK_ADD_SOURCE,
        itemCount: itemCount,
        variantId: String(variantId),
        sections: sections,
      },
    };

    document.dispatchEvent(event);
  }

  /**
   * Refreshes header cart bubble text and visibility classes.
   * @param {number} itemCount - Total items count.
   */
  function updateHeaderCartBubble(itemCount) {
    const hasCartItems = typeof itemCount === 'number' && itemCount > 0;

    try {
      const bubbleTextElements = document.querySelectorAll(SELECTORS.cartBubbleTextCount);
      bubbleTextElements.forEach((element) => {
        element.textContent = `(${itemCount})`;
        element.classList.toggle('hidden', !hasCartItems);
      });

      const bubbleScreenReaderElements = document.querySelectorAll(SELECTORS.cartBubbleScreenReader);
      bubbleScreenReaderElements.forEach((element) => {
        element.textContent = `Total items in cart: ${itemCount}`;
      });

      const bubbleElements = document.querySelectorAll(SELECTORS.cartBubble);
      bubbleElements.forEach((element) => {
        element.classList.toggle('visually-hidden', !hasCartItems);
      });

      const headerCartIcons = document.querySelectorAll(SELECTORS.headerCartIcon);
      headerCartIcons.forEach((element) => {
        element.classList.toggle('header-actions__cart-icon--has-cart', hasCartItems);
      });
    } catch (error) {
      console.warn('Could not refresh header cart bubble:', error);
    }
  }

  /**
   * Reads the item count currently rendered in the header cart bubble.
   * Used as a fallback when /cart.js cannot be reached.
   * @returns {number} The rendered count, or 0 when it cannot be parsed.
   */
  function readRenderedCartCount() {
    const bubbleText = document.querySelector(SELECTORS.cartBubbleTextCount);
    const match = bubbleText && bubbleText.textContent ? bubbleText.textContent.match(/\d+/) : null;

    return match ? Number(match[0]) : 0;
  }

  // =========================================================================
  // Event Handlers
  // =========================================================================

  /**
   * Clears any accidental text selection ranges caused by double-taps/clicks.
   */
  function clearTextSelection() {
    try {
      if (window.getSelection) {
        const selection = window.getSelection();
        if (selection && selection.empty) {
          selection.empty();
        } else if (selection && selection.removeAllRanges) {
          selection.removeAllRanges();
        }
      }
    } catch (e) {
      // Ignore if selection API unavailable
    }
  }

  /**
   * Handles click on the quick add trigger button.
   *
   * Toggles from any state, including 'success': a shopper who has just added one size
   * must still be able to reopen the picker and add another.
   *
   * @param {Event} event - Trigger click event.
   * @param {HTMLElement} triggerBtn - Quick add trigger button.
   */
  function handleTriggerClick(event, triggerBtn) {
    if (!triggerBtn) return;

    clearTextSelection();

    const card = triggerBtn.closest(SELECTORS.card);
    if (!card) return;

    event.preventDefault();
    event.stopPropagation();

    const isOpen = card.classList.contains(CARD_STATES.open);
    if (isOpen) {
      updateCardState(card, 'idle');
      updateTriggerButtonState(triggerBtn, 'idle');
    } else {
      // Close any other open card first
      closeAllOpenCards(card);
      updateCardState(card, 'open');
      updateTriggerButtonState(triggerBtn, 'open');
    }
  }

  /**
   * Handles selection of a variant size button.
   * @param {Event} event - Size button click event.
   * @param {HTMLElement} sizeBtn - Size button clicked.
   */
  async function handleSizeOptionClick(event, sizeBtn) {
    if (!sizeBtn) return;

    event.preventDefault();
    event.stopPropagation();
    clearTextSelection();

    const isButtonDisabled = sizeBtn.hasAttribute('disabled') || sizeBtn.getAttribute('aria-disabled') === 'true';
    if (isButtonDisabled) return;

    const isRequestInFlight = sizeBtn.getAttribute('data-is-adding') === 'true';
    if (isRequestInFlight) {
      return;
    }

    let variantId = sizeBtn.getAttribute('data-variant-id');
    if (!variantId && sizeBtn.href) {
      try {
        const url = new URL(sizeBtn.href, window.location.origin);
        variantId = url.searchParams.get('variant');
      } catch (e) {
        console.warn('Failed to parse variant url:', e);
      }
    }

    if (!variantId) return;

    const card = sizeBtn.closest(SELECTORS.card);
    if (!card) return;

    const triggerBtn = card.querySelector(SELECTORS.trigger);
    if (!triggerBtn) return;

    sizeBtn.setAttribute('data-is-adding', 'true');
    updateCardState(card, 'adding');
    updateTriggerButtonState(triggerBtn, 'adding', 'Adding...');

    const previousCartCount = readRenderedCartCount();

    try {
      const responseData = await addItemToCart(variantId, 1, getCartSectionIds());

      // The add itself succeeded past this point, so a failed count lookup must not
      // be treated as an add-to-cart failure.
      let cartData = null;
      try {
        cartData = await getCartDetails();
      } catch (error) {
        console.warn('Could not fetch cart details:', error);
      }

      const itemCount =
        cartData && typeof cartData.item_count === 'number' ? cartData.item_count : previousCartCount + 1;

      dispatchCartUpdate({
        cart: cartData,
        sections: responseData.sections,
        variantId: variantId,
        itemCount: itemCount,
      });

      // Runs after the dispatch: the theme's cart-icon writes a bare count, this
      // restores the "(n)" formatting used across the store.
      updateHeaderCartBubble(itemCount);

      updateCardState(card, 'success');
      updateTriggerButtonState(triggerBtn, 'success', 'Added to Cart');
    } catch (error) {
      console.error('Error adding variant to cart:', error);
      updateCardState(card, 'open');
      updateTriggerButtonState(triggerBtn, 'open');
    } finally {
      sizeBtn.setAttribute('data-is-adding', 'false');
    }
  }

  /**
   * Closes all currently open quick add panels except optional excluded card.
   * @param {HTMLElement} [excludeCard=null]
   */
  function closeAllOpenCards(excludeCard = null) {
    const openCards = document.querySelectorAll(`.${CARD_STATES.open}`);
    openCards.forEach((openCard) => {
      if (excludeCard && openCard === excludeCard) return;
      updateCardState(openCard, 'idle');
      const triggerBtn = openCard.querySelector(SELECTORS.trigger);
      if (triggerBtn) {
        updateTriggerButtonState(triggerBtn, 'idle');
      }
    });
  }

  /**
   * Handles click outside any active quick add cards.
   * @param {MouseEvent} event
   */
  function handleDocumentClick(event) {
    const target = event.target;
    if (!target) return;

    const openCards = document.querySelectorAll(`.${CARD_STATES.open}`);
    if (!openCards.length) return;

    openCards.forEach((card) => {
      if (!card.contains(target)) {
        updateCardState(card, 'idle');
        const triggerBtn = card.querySelector(SELECTORS.trigger);
        if (triggerBtn) {
          updateTriggerButtonState(triggerBtn, 'idle');
        }
      }
    });
  }

  // =========================================================================
  // Initialization & Event Delegation
  // =========================================================================

  /**
   * Dynamically positions carousel arrows centered on the product image gallery.
   */
  function syncCarouselArrowPosition() {
    try {
      const carousels = document.querySelectorAll('.resource-list__carousel');
      carousels.forEach((carousel) => {
        const firstGallery = carousel.querySelector('.card-gallery');
        const arrows = carousel.querySelector('slideshow-arrows');
        if (firstGallery && arrows) {
          const galleryHeight = firstGallery.offsetHeight;
          if (galleryHeight > 0) {
            // 16px is the top padding on slideshow-slides
            const centerY = (galleryHeight / 2) + 16;
            arrows.style.setProperty('--arrow-top', `${centerY}px`);
          }
        }
      });
    } catch (e) {
      // Ignore if DOM calculation fails
    }
  }

  /**
   * Attaches global delegated event listeners.
   */
  function initQuickAddEvents() {
    ['pointerdown', 'touchstart', 'mousedown'].forEach((eventType) => {
      document.addEventListener(eventType, (event) => {
        if (
          event.target.closest(SELECTORS.trigger) ||
          event.target.closest(SELECTORS.sizeButton) ||
          event.target.closest(SELECTORS.card) ||
          event.target.closest('.tabbed-collection__tab')
        ) {
          clearTextSelection();
        }
      }, { passive: true });
    });

    document.addEventListener('click', (event) => {
      const slideshowBtn = event.target.closest('.slideshow-control');
      if (slideshowBtn) {
        event.stopPropagation();
        return;
      }

      const triggerBtn = event.target.closest(SELECTORS.trigger);
      if (triggerBtn) {
        handleTriggerClick(event, triggerBtn);
        return;
      }

      const sizeBtn = event.target.closest(SELECTORS.sizeButton);
      if (sizeBtn) {
        handleSizeOptionClick(event, sizeBtn);
        return;
      }

      const tabBtn = event.target.closest('.tabbed-collection__tab');
      if (tabBtn) {
        setTimeout(syncCarouselArrowPosition, 50);
      }

      handleDocumentClick(event);
    }, true);

    syncCarouselArrowPosition();
    initSplitSectionHeightSync();

    window.addEventListener('resize', syncCarouselArrowPosition, { passive: true });

    // Observe potential dynamic container mutations (e.g. infinite scroll, collection filter changes)
    if ('MutationObserver' in window) {
      let syncPending = false;

      const observer = new MutationObserver(() => {
        // Both callbacks read layout, so running them per mutation thrashes while
        // cards animate. Coalesce a burst of mutations into one measure per frame.
        if (syncPending) return;
        syncPending = true;

        window.requestAnimationFrame(() => {
          syncPending = false;
          syncCarouselArrowPosition();
          initSplitSectionHeightSync();
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  let splitSectionHeightSyncBound = false;

  /**
   * Dynamically synchronizes left hero banner height with the right tabbed product list.
   * Ensures neither cards nor open size swatches are ever clipped/cut off across all viewports.
   */
  function initSplitSectionHeightSync() {
    try {
      const tabbedSection = document.querySelector('[id*="tabbed_product_list"]');
      const heroSection = document.querySelector('[id*="hero_rYErR6"]');

      if (!tabbedSection || !heroSection) return;

      const updateHeight = () => {
        try {
          if (window.innerWidth >= 990) {
            const height = Math.round(tabbedSection.getBoundingClientRect().height);
            if (height > 0) {
              heroSection.style.setProperty('--split-sync-height', `${height}px`);
            }
          } else {
            heroSection.style.removeProperty('--split-sync-height');
          }
        } catch (err) {
          // Defensive guard
        }
      };

      // This runs on every DOM mutation. Binding observers here unconditionally
      // stacked up a new ResizeObserver and resize listener each time, so hovering
      // or opening a size picker left dozens of them all re-measuring on each
      // layout change. Bind once; later calls just re-measure.
      if (splitSectionHeightSyncBound) {
        updateHeight();
        return;
      }

      splitSectionHeightSyncBound = true;

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(tabbedSection);
      }

      window.addEventListener('resize', updateHeight, { passive: true });
      updateHeight();
    } catch (error) {
      // Defensive guard
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickAddEvents);
  } else {
    initQuickAddEvents();
  }
})();
